import {
  EscalationReason,
  ThreadAIOutcome,
  TriggerType,
} from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { ThreadAIActivity } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { IconColorType } from '@frontend/icons';

export type ThreadAiNotificationCategory = 'session-complete' | 'needs-attention';

export type ThreadAiNotificationClassification = {
  category: ThreadAiNotificationCategory;
  label: string;
  iconName: 'check' | 'calendar-scheduled' | 'calendar-rescheduled' | 'calendar-canceled' | 'aineeds-attention';
  iconColor?: IconColorType;
  customIconColor?: string;
};

const SUCCESSFULLY_ASSISTED_OUTCOMES = new Set<ThreadAIOutcome>([
  ThreadAIOutcome.THREAD_AI_OUTCOME_ASSISTANCE_COMPLETE,
  ThreadAIOutcome.THREAD_AI_OUTCOME_APPOINTMENT_CONFIRMATION,
  ThreadAIOutcome.THREAD_AI_OUTCOME_FAQ_ASSISTANCE_COMPLETE,
]);

const ADDED_TO_SCHEDULE_OUTCOMES = new Set<ThreadAIOutcome>([
  ThreadAIOutcome.THREAD_AI_OUTCOME_BOOKING_COMPLETION,
  ThreadAIOutcome.THREAD_AI_OUTCOME_SCHEDULING_COMPLETE,
]);

const SCHEDULING_INCOMPLETE_REASONS = new Set<EscalationReason>([
  EscalationReason.ESCALATION_REASON_SERVICE_STAFF_NOT_FOUND,
  EscalationReason.ESCALATION_REASON_BOOKING_DISALLOWED,
  EscalationReason.ESCALATION_REASON_BOOKING_FORWARD,
  EscalationReason.ESCALATION_REASON_RESCHEDULE_FORWARD,
  EscalationReason.ESCALATION_REASON_CANCEL_FORWARD,
  EscalationReason.ESCALATION_REASON_NO_AVAILABILITY_FOUND,
  EscalationReason.ESCALATION_REASON_BOOKING_HELP_NEEDED,
  EscalationReason.ESCALATION_REASON_RESCHEDULE_HELP_NEEDED,
  EscalationReason.ESCALATION_REASON_CANCEL_HELP_NEEDED,
  EscalationReason.ESCALATION_REASON_BOOKING_FAILURE,
  EscalationReason.ESCALATION_REASON_CONFIRMATION_FAILURE,
]);

const UNABLE_TO_ASSIST_REASONS = new Set<EscalationReason>([
  EscalationReason.ESCALATION_REASON_CALLBACK_CUSTOMER,
  EscalationReason.ESCALATION_REASON_CUSTOMER_UNRESPONSIVE,
  EscalationReason.ESCALATION_REASON_LANDLINE_CALL,
  EscalationReason.ESCALATION_REASON_UNRESPONSIVE_VOICEMAIL,
  EscalationReason.ESCALATION_REASON_RUNNING_LATE,
  EscalationReason.ESCALATION_REASON_BOOKING_NOT_FOUND,
]);

export const getThreadAiNotificationClassification = (
  aiItem: ThreadAIActivity
): ThreadAiNotificationClassification | null => {
  if (
    aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_OUTCOME &&
    aiItem.status !== undefined &&
    SUCCESSFULLY_ASSISTED_OUTCOMES.has(aiItem.status)
  ) {
    return {
      category: 'session-complete',
      label: 'Successfully assisted!',
      iconName: 'check',
      iconColor: 'success',
    };
  }

  if (
    aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_OUTCOME &&
    aiItem.status !== undefined &&
    ADDED_TO_SCHEDULE_OUTCOMES.has(aiItem.status)
  ) {
    return {
      category: 'session-complete',
      label: 'Added to schedule!',
      iconName: 'calendar-scheduled',
      iconColor: 'primary',
    };
  }

  if (
    aiItem.status === ThreadAIOutcome.THREAD_AI_OUTCOME_RESCHEDULING_COMPLETION &&
    aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_OUTCOME
  ) {
    return {
      category: 'session-complete',
      label: 'Rescheduled',
      iconName: 'calendar-rescheduled',
      iconColor: 'success',
    };
  }

  if (
    aiItem.status === ThreadAIOutcome.THREAD_AI_OUTCOME_CANCELLATION_COMPLETION &&
    aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_OUTCOME
  ) {
    return {
      category: 'session-complete',
      label: 'Canceled',
      iconName: 'calendar-canceled',
      customIconColor: '#C25700',
    };
  }

  if (
    aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_ESCALATED &&
    aiItem.escalationReason !== undefined &&
    SCHEDULING_INCOMPLETE_REASONS.has(aiItem.escalationReason)
  ) {
    return {
      category: 'needs-attention',
      label: 'Scheduling incomplete',
      iconName: 'aineeds-attention',
      iconColor: 'default',
    };
  }

  if (
    aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_ESCALATED &&
    aiItem.escalationReason !== undefined &&
    UNABLE_TO_ASSIST_REASONS.has(aiItem.escalationReason)
  ) {
    return {
      category: 'needs-attention',
      label: 'Unable to assist this patient',
      iconName: 'aineeds-attention',
      iconColor: 'default',
    };
  }

  if (aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_ESCALATED) {
    return {
      category: 'needs-attention',
      label: 'Unable to assist this patient',
      iconName: 'aineeds-attention',
      iconColor: 'default',
    };
  }

  return null;
};
